import React, { useState } from "react";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { NonEditableFormField } from "../pages/reservations/guests/components/form/FormFields";
import { Matcher } from "react-day-picker";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  date: Date | null;
  onDateChange: (date: Date) => void;
  className?: string;
  labelClassName?: string;
  contentClassName?: string;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  disabled?: Matcher[] | Matcher;
  formatString?: string;
  isEdit?: boolean;
  showIcon?: boolean;
  icon?: React.ReactNode;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  date,
  minDate,
  maxDate,
  onDateChange,
  className,
  contentClassName,
  disabled,
  placeholder = "Select date",
  isEdit = true,
  formatString = "d MMM",
  showIcon = true,
  icon = <CalendarIcon className="size-4" />,
}) => {
  const [open, setOpen] = useState(false);

  const formatDate = (
    date: Date | undefined,
    formatOption: string = "d MMM",
  ): string => {
    if (!date) {
      return placeholder;
    }
    return format(date, formatString || formatOption, { locale: enUS });
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) onDateChange(selectedDate);
    setOpen(false);
  };

  if (!isEdit) {
    return (
      <NonEditableFormField
        value={formatDate(date ?? undefined, "dd. MM. yyyy")}
        isCopy={false}
        contentClassName={contentClassName}
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="new_outline"
          className={cn("justify-start", className)}
        >
          {showIcon && icon}
          {formatDate(date ?? undefined)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={date ?? undefined}
          captionLayout="dropdown"
          onSelect={handleDateSelect}
          locale={enUS}
          disabled={disabled}
          hidden={[
            ...(minDate ? [{ before: minDate }] : []),
            ...(maxDate ? [{ after: maxDate }] : []),
          ]}
        />
      </PopoverContent>
    </Popover>
  );
};
